import { useState } from "react";
import { Inbox } from "lucide-react";
import { PageHeader } from "@/components/layout";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from "@/components/ui";
import { useDisclosure } from "@/hooks";

const selectOptions = [
  { label: "Active", value: "active" },
  { label: "Paused", value: "paused" },
  { label: "Archived", value: "archived" },
];

export default function UiKitPage() {
  const modal = useDisclosure();
  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");
  const [checked, setChecked] = useState(true);

  return (
    <div className="space-y-6">
      <PageHeader
        title="UI kit"
        description="Shared primitives live in src/components/ui. Reuse these instead of one-off markup."
      />

      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
          <CardDescription>Variants, sizes, and loading state.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
          <Button size="sm">Small</Button>
          <Button loading>Saving</Button>
          <Button onClick={modal.onOpen}>Open modal</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
          <CardDescription>Form primitives with label, hint, and error slots.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Name" htmlFor="name" hint="Used on the sample form only.">
            <Input
              id="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Jane Doe"
            />
          </Field>
          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              options={selectOptions}
            />
          </Field>
          <Field label="Notes" htmlFor="notes" className="md:col-span-2">
            <Textarea id="notes" placeholder="Optional notes" />
          </Field>
          <Checkbox
            id="notify"
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
            label="Send notifications"
          />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge>Default</Badge>
              <Badge variant="primary">Primary</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
              <Badge variant="danger">Danger</Badge>
              <Avatar name="Papaya Web" />
              <Spinner />
            </div>
            <Alert title="Ready" variant="info">
              The design system is wired to CSS tokens, including dark mode.
            </Alert>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>

        <EmptyState
          icon={<Inbox className="h-8 w-8" />}
          title="No records yet"
          description="Empty states belong in the shared kit so every feature looks consistent."
          action={<Button variant="outline">Create one later</Button>}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Table</CardTitle>
        </CardHeader>
        <Table>
          <THead>
            <TR>
              <TH>Layer</TH>
              <TH>Role</TH>
              <TH>Owns business logic</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>pages</TD>
              <TD>Route screens</TD>
              <TD>No</TD>
            </TR>
            <TR>
              <TD>features</TD>
              <TD>Domain modules</TD>
              <TD>Yes</TD>
            </TR>
            <TR>
              <TD>components/ui</TD>
              <TD>Reusable primitives</TD>
              <TD>No</TD>
            </TR>
          </TBody>
        </Table>
      </Card>

      <Modal
        open={modal.open}
        onClose={modal.onClose}
        title="Shared modal"
        footer={
          <>
            <Button variant="outline" onClick={modal.onClose}>
              Cancel
            </Button>
            <Button onClick={modal.onClose}>Confirm</Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          Dialogs, overlays, and confirmations should come from this primitive.
        </p>
      </Modal>
    </div>
  );
}
